import { useState } from "react";
import { analyzeClient, saveItem } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Sparkles, AlertTriangle, Save, ArrowLeft, Target, BarChart3, Flag, ArrowRight, ScanLine } from "lucide-react";
import { toast } from "sonner";

const fieldBase = "mt-2 bg-white rounded-2xl h-12 border border-black/10 focus-visible:ring-brand-purple";
const taBase = "mt-2 bg-white rounded-2xl border border-black/10 resize-none focus-visible:ring-brand-purple";
const lbl = "text-sm font-semibold text-foreground/85";

function ScoreBar({ label, value }) {
  const v = Number(value ?? 0);
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="capitalize text-foreground/65">{label.replace(/_/g, " ")}</span>
        <span className="font-display font-black">{v}</span>
      </div>
      <Progress value={v} className="h-1.5 bg-black/[0.06] [&>div]:bg-foreground" />
    </div>
  );
}

function FormView({ form, setForm, onAnalyze, loading, errorField }) {
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const errCls = (k) => (errorField === k ? "!border-rose-500 ring-1 ring-rose-500/40" : "");
  return (
    <div className="max-w-4xl mx-auto">
      <header className="mb-10 relative">
        <div className="blob blob-lemon w-[220px] h-[220px] -top-8 left-10" />
        <div className="blob blob-coral w-[200px] h-[200px] top-0 right-1/4" style={{ animationDelay: "2s" }} />
        <div className="relative">
          <span className="tag-pill tag-pill-amber mb-5"><ScanLine className="w-3 h-3" /> Analizar cliente</span>
          <h1 className="font-display text-4xl md:text-6xl font-black tracking-[-0.04em] leading-[0.95]">
            ¿Vale la pena<br />
            <span className="scribble-underline scribble-lavender">aceptar</span> a este cliente?
          </h1>
          <p className="text-base text-foreground/65 mt-5 max-w-2xl leading-relaxed">
            Pega web, redes sociales, brief o tus notas. Detecto madurez de marca, riesgos reales y si valora estrategia o solo busca precio.
          </p>
        </div>
      </header>

      <div className="app-card p-7 md:p-9 space-y-6">
        <div>
          <Label className={lbl}>Web del cliente</Label>
          <Input data-testid="input-website" value={form.website} onChange={upd("website")} placeholder="https://..." className={fieldBase} />
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          <div>
            <Label className={lbl}>Instagram</Label>
            <Input data-testid="input-instagram" value={form.instagram} onChange={upd("instagram")} placeholder="@..." className={fieldBase} />
          </div>
          <div>
            <Label className={lbl}>TikTok</Label>
            <Input data-testid="input-tiktok" value={form.tiktok} onChange={upd("tiktok")} placeholder="@..." className={fieldBase} />
          </div>
          <div>
            <Label className={lbl}>LinkedIn</Label>
            <Input data-testid="input-linkedin" value={form.linkedin} onChange={upd("linkedin")} placeholder="URL" className={fieldBase} />
          </div>
        </div>

        <div>
          <Label className={lbl}>Descripción del negocio</Label>
          <Textarea id="field-analyzer-description" data-testid="input-description" rows={3} value={form.description} onChange={upd("description")} placeholder="A qué se dedica, modelo, mercado, momento actual..." className={`${taBase} ${errCls("description")}`} />
          {errorField === "description" && <p className="text-xs text-rose-600 mt-1.5 font-medium">Añade al menos una descripción o brief del cliente para empezar.</p>}
        </div>

        <div>
          <Label className={lbl}>Brief del cliente</Label>
          <Textarea data-testid="input-brief" rows={4} value={form.brief} onChange={upd("brief")} placeholder="Pega aquí el brief o los requerimientos que te envió el cliente..." className={taBase} />
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <Label className={lbl}>Objetivos del cliente</Label>
            <Textarea data-testid="input-objectives" rows={3} value={form.objectives} onChange={upd("objectives")} className={taBase} />
          </div>
          <div>
            <Label className={lbl}>Tus notas</Label>
            <Textarea data-testid="input-notes" rows={3} value={form.notes} onChange={upd("notes")} placeholder="Lo que has notado del cliente (tono, prisas, dudas, vibes)..." className={taBase} />
          </div>
        </div>

        <button
          onClick={onAnalyze}
          disabled={loading}
          data-testid="analyze-btn"
          className="btn-apple w-full justify-center text-base h-14"
        >
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Analizando...</> : <><Sparkles className="w-4 h-4" /> Analizar cliente <ArrowRight className="w-4 h-4" /></>}
        </button>
      </div>
    </div>
  );
}

function ResultView({ data, onReset, onSave }) {
  const total = data.client_power_score?.total ?? 0;
  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={onReset} className="btn-apple-outline text-sm" data-testid="back-to-form-analyzer">
          <ArrowLeft className="w-3.5 h-3.5" /> Editar datos
        </button>
        <button onClick={onSave} className="btn-apple text-sm" data-testid="save-client-btn">
          <Save className="w-3.5 h-3.5" /> Guardar análisis
        </button>
      </div>

      {/* Hero score */}
      <div className="app-card-dark p-10 relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-60 h-60 rounded-full bg-[#F4A89C]/15 blur-2xl" />
        <div className="absolute -bottom-10 -left-10 w-60 h-60 rounded-full bg-[#A8D5BA]/15 blur-2xl" />
        <div className="relative grid md:grid-cols-[auto_1fr] gap-8 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-wider font-bold opacity-65">Score del cliente</div>
            <div className="font-display text-7xl md:text-8xl font-black leading-none mt-1 tracking-[-0.04em]" data-testid="client-power-score">{total}</div>
            <div className="font-display text-xl font-black mt-2 opacity-85">{data.client_power_score?.label}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider font-bold opacity-65 mb-2">Recomendación estratégica</div>
            <p className="text-base md:text-lg leading-relaxed font-medium opacity-95">{data.recommended_approach}</p>
          </div>
        </div>
      </div>

      <Tabs defaultValue="scores" className="w-full">
        <TabsList className="bg-white border border-black/[0.08] rounded-2xl p-1 grid grid-cols-3 h-auto">
          <TabsTrigger value="scores" data-testid="tab-scores" className="rounded-xl data-[state=active]:bg-foreground data-[state=active]:text-white py-2.5 text-xs font-semibold">
            <BarChart3 className="w-3.5 h-3.5 mr-2" /> Puntuaciones
          </TabsTrigger>
          <TabsTrigger value="risks" data-testid="tab-risks" className="rounded-xl data-[state=active]:bg-foreground data-[state=active]:text-white py-2.5 text-xs font-semibold">
            <AlertTriangle className="w-3.5 h-3.5 mr-2" /> Riesgos
          </TabsTrigger>
          <TabsTrigger value="flags" data-testid="tab-flags" className="rounded-xl data-[state=active]:bg-foreground data-[state=active]:text-white py-2.5 text-xs font-semibold">
            <Flag className="w-3.5 h-3.5 mr-2" /> Señales
          </TabsTrigger>
        </TabsList>

        <TabsContent value="scores" className="mt-6 space-y-4">
          <div className="app-card p-7">
            <div className="tag-pill mb-4">Desglose del Score</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
              {Object.entries(data.client_power_score?.breakdown || {}).map(([k, v]) => (
                <ScoreBar key={k} label={k} value={v} />
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 items-stretch">
            {[
              { k: "brand_maturity", l: "Madurez de marca", tag: "tag-pill-mint" },
              { k: "visual_sophistication", l: "Sofisticación visual", tag: "tag-pill-lavender" },
              { k: "positioning_clarity", l: "Claridad de posicionamiento", tag: "tag-pill-rose" },
              { k: "communication_quality", l: "Calidad de comunicación", tag: "tag-pill-amber" },
            ].map(({ k, l, tag }) => {
              const d = data[k];
              if (!d) return null;
              return (
                <div key={k} className="app-card p-6 h-[240px] overflow-y-auto custom-scroll">
                  <div className={`tag-pill ${tag} mb-3`}>{l}</div>
                  <div className="flex items-baseline gap-3">
                    <div className="font-display text-5xl font-black tracking-[-0.03em]">{d.score}</div>
                    <span className="text-[11px] font-semibold text-foreground/55">{d.label}</span>
                  </div>
                  <p className="text-xs mt-3 leading-relaxed text-foreground/65">{d.evidence}</p>
                </div>
              );
            })}
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            <div className="app-card p-5">
              <div className="text-[10px] uppercase tracking-wider font-bold text-foreground/55 mb-2">Nivel estratégico requerido</div>
              <div className="font-display text-xl font-black">{data.required_strategic_level}</div>
            </div>
            <div className="app-card p-5">
              <div className="text-[10px] uppercase tracking-wider font-bold text-foreground/55 mb-2">Complejidad del proyecto</div>
              <div className="font-display text-xl font-black">{data.project_complexity}</div>
            </div>
            <div className="app-card p-5">
              <div className="text-[10px] uppercase tracking-wider font-bold text-foreground/55 mb-2">Soporte esperado</div>
              <div className="font-display text-xl font-black">{data.expected_support_level}</div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="risks" className="mt-6">
          <div className="grid md:grid-cols-2 gap-4 items-stretch">
            <div className="app-card p-6 h-[380px] overflow-y-auto custom-scroll">
              <div className="tag-pill tag-pill-rose mb-3">
                <AlertTriangle className="w-3 h-3" strokeWidth={2.5} />
                Riesgo de sobretrabajo
              </div>
              <div className="flex items-baseline gap-3">
                <div className="font-display text-5xl font-black tracking-[-0.03em]">{data.scope_creep_risk?.score}</div>
                <span className="text-[11px] font-semibold text-foreground/55">{data.scope_creep_risk?.level}</span>
              </div>
              <ul className="mt-4 space-y-1.5">
                {data.scope_creep_risk?.reasons?.map((r, i) => <li key={i} className="text-xs text-foreground/70">— {r}</li>)}
              </ul>
            </div>
            <div className="app-card-dark p-6 h-[380px] overflow-y-auto custom-scroll">
              <div className="tag-pill tag-pill-dark mb-3">
                <Target className="w-3 h-3" strokeWidth={2.5} />
                ¿Estrategia o precio?
              </div>
              <div className="font-display text-2xl font-black mb-3">{data.values_strategy_vs_price}</div>
              <div className="border-t border-white/15 pt-3">
                <div className="text-[10px] uppercase tracking-wider font-bold opacity-65 mb-1">Brecha de valor</div>
                <p className="text-sm font-bold">{data.value_gap?.verdict}</p>
                <p className="text-xs opacity-80 mt-2 leading-relaxed">{data.value_gap?.explanation}</p>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="flags" className="mt-6">
          <div className="grid md:grid-cols-2 gap-4 items-stretch">
            <div className="app-card p-6 h-[320px] overflow-y-auto custom-scroll">
              <div className="tag-pill tag-pill-rose mb-3">Señales de alerta</div>
              <ul className="space-y-2.5 mt-2">
                {data.red_flags?.length
                  ? data.red_flags.map((x, i) => <li key={i} className="text-sm flex gap-2"><span className="text-[#C63F3E] mt-0.5">●</span>{x}</li>)
                  : <li className="text-sm text-foreground/60">Sin señales de alerta. Buena pinta.</li>}
              </ul>
            </div>
            <div className="app-card p-6 h-[320px] overflow-y-auto custom-scroll">
              <div className="tag-pill tag-pill-mint mb-3">Señales positivas</div>
              <ul className="space-y-2.5 mt-2">
                {data.green_flags?.length
                  ? data.green_flags.map((x, i) => <li key={i} className="text-sm flex gap-2"><span className="text-emerald-600 mt-0.5">●</span>{x}</li>)
                  : <li className="text-sm text-foreground/60">Aún sin señales claras.</li>}
              </ul>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default function ClientAnalyzer() {
  const [form, setForm] = useState({
    website: "", instagram: "", tiktok: "", linkedin: "",
    description: "", brief: "", received_proposal: "",
    objectives: "", notes: "", visual_references: "",
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [errorField, setErrorField] = useState(null);

  const onAnalyze = async () => {
    const hasInput = Object.values(form).some((v) => v && v.trim());
    if (!hasInput) {
      setErrorField("description");
      toast.error("Añade al menos una descripción o brief del cliente");
      setTimeout(() => {
        const el = document.getElementById("field-analyzer-description");
        if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.focus(); }
      }, 100);
      return;
    }
    setErrorField(null);
    setLoading(true);
    try {
      const r = await analyzeClient(form);
      setData(r);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("Análisis listo");
    } catch (e) {
      console.error(e);
      toast.error(e.userMessage || "Error analizando el cliente.");
    } finally {
      setLoading(false);
    }
  };

  const onSave = async () => {
    if (!data) return;
    const title = form.description?.slice(0, 60) || form.website || "Cliente sin nombre";
    try {
      await saveItem({ kind: "client", title, payload: { input: form, output: data } });
      toast.success("Análisis guardado");
    } catch {
      toast.error("No se pudo guardar");
    }
  };

  const onReset = () => { setData(null); window.scrollTo({ top: 0, behavior: "smooth" }); };

  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center relative">
        <div className="blob blob-lemon w-[300px] h-[300px] top-0 left-1/3" />
        <div className="blob blob-coral w-[300px] h-[300px] bottom-0 right-1/3" style={{ animationDelay: "2s" }} />
        <div className="text-center max-w-sm relative">
          <div className="w-14 h-14 mx-auto rounded-full bg-foreground grid place-items-center mb-6">
            <Loader2 className="w-6 h-6 animate-spin text-white" />
          </div>
          <h2 className="font-display text-2xl font-black tracking-[-0.02em]">Leyendo señales del cliente...</h2>
          <p className="text-sm text-foreground/65 mt-3">Construyendo el Score y detectando riesgos.</p>
        </div>
      </div>
    );
  }

  if (data) return <ResultView data={data} onReset={onReset} onSave={onSave} />;
  return <FormView form={form} setForm={setForm} onAnalyze={onAnalyze} loading={loading} errorField={errorField} />;
}
